import * as React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import {
    VerificationService,
    FirstResponderPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    Organization,
    FormSelectChoice,
    FirstResponderPersonalInfoResponse,
    getFieldValidationErrors,
    isFormErrored,
    logger,
    FormFooterComponent,
    FirstNameComponent,
    LastNameComponent,
    EmailComponent,
    FirstResponderOrganizationComponent,
    FirstResponderStatusDefaultMessagesEnum,
    updateFieldValidationErrorsByFieldId,
    MarketConsentWrapper,
    getCustomValidator,
    getSafe,
    validateFieldById,
    FieldIdEnum,
    FieldId,
    FormValidationOptions,
    ViewModel,
    getCustomValidatorFields,
} from '@sheerid/jslib';
import { BirthDate } from './BirthDate2Component';
import { LogoHeaderComponent } from './LogoHeaderComponent';
import { FirstResponderStatusComponent } from './FirstResponderStatusComponent2';
import { PostalCodeComponent } from './PostalCodeComponent';


interface StepFirstResponderPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

export const StepFirstResponderPersonalInfo = ({ intl, verificationService }: StepFirstResponderPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as FirstResponderPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;

    const updateViewModel = (key, value) => {
        const nextState = produce(viewModel, (draft) => {
            draft[key] = value;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateViewModelOrganization = (choice: Organization) => {
        const nextState = produce(viewModel, (draft: FirstResponderPersonalInfoViewModel) => {
            draft.organization.id = choice.id;
            draft.organization.name = choice.name;
        });
        verificationService.updateViewModel(nextState);
    };

    const updateFieldValidationErrors = (fieldValidationErrors: FieldValidationErrors) => {
        verificationService.updateFieldValidationErrors(fieldValidationErrors);
    };

    const getAvailableFirstResponderStatuses = ():FormSelectChoice[] => {
        const availableStatusesResponse =
            (verificationService.verificationResponse as FirstResponderPersonalInfoResponse).availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: FirstResponderStatusDefaultMessagesEnum[status],
            });
        }
        return availableStatuses;
    };

    const submitForm = () => {
        const currentFieldValidationErrors = getFieldValidationErrors(viewModel);
        const isFormValid = !isFormErrored(currentFieldValidationErrors);

        if (isFormValid) {
            const nextState = produce(viewModel, () => {});
            logger.info('StepFirstResponderPersonalInfoComponent submitting form');
            verificationService.submitStep(
                'collectFirstResponderPersonalInfo',
                nextState,
                verificationService.verificationResponse,
            );
        } else {
            updateFieldValidationErrors(currentFieldValidationErrors);
        }
    };

    const isFormFilled = (
        viewModel: ViewModel,
        formValidationOptions?: FormValidationOptions,
      ): boolean => {
        const customValidatorFields = getCustomValidatorFields();

        // TODO fix next line
        // eslint-disable-next-line
        for (const fieldId in viewModel) {
          const fieldValue = viewModel[fieldId] ? viewModel[fieldId] : "";
          const isFieldSkipped =
            viewModel.fieldsToSkipValidation &&
            viewModel.fieldsToSkipValidation.includes(fieldId as FieldId);

          if (
            FieldIdEnum[fieldId] &&
            typeof validateFieldById(fieldId, fieldValue, formValidationOptions) === "string" &&
            !isFieldSkipped
          ) {
            return false;
          }
        }

        const metadata = getSafe(() => (viewModel as any).metadata, null);
        if (metadata !== null) {
          // TODO fix next line
          // eslint-disable-next-line
          for (const fieldId in metadata) {
            const fieldValue = metadata[fieldId] !== undefined ? metadata[fieldId] : "";

            if (
              customValidatorFields.includes(fieldId) &&
              typeof getCustomValidator(fieldId)(fieldValue, formValidationOptions) === "string"
            ) {
              return false;
            }
          }
        }

        return true;
    };

    return (
        <div id="sid-step-active-first-responder-personal-info" className="sid-l-container">
            <div className="sid-header">
                <LogoHeaderComponent/>
                <div className="sid-header__subtitle sid-l-space-top-lg">
                    <FormattedHTMLMessage id="step.personalInfo.subtitle"
                        defaultMessage="Confirm your eligibility by simply completing the form below." />
                </div>
                <div className="sid-header__title sid-l-space-top-lg">
                    <h1>
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Unlock this Teaching Staff-Only Offer" />
                    </h1>
                </div>
            </div>
            <div className="sid-l-space-top-lg">
                <FormattedHTMLMessage id="step.personalInfo.requiredFields" defaultMessage="All fields are required." />
            </div>

            <FirstResponderStatusComponent
                value={{
                    value: (verificationService.viewModel as FirstResponderPersonalInfoViewModel).status,
                    label: FirstResponderStatusDefaultMessagesEnum[(verificationService.viewModel as FirstResponderPersonalInfoViewModel).status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableFirstResponderStatuses() }
                onChange={
                    (status) => {
                        updateViewModel('status', status);
                        updateFieldValidationErrorsByFieldId('status', status, verificationService);
                    }
                }
            />

            <FirstResponderOrganizationComponent
                isErrored={!!fieldValidationErrors.organization}
                verificationService={verificationService}
                placeholder={
                    intl.formatMessage({
                        id: "organization",
                        defaultMessage: "Organization",
                        })
                }
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as FirstResponderPersonalInfoViewModel).organization}
            />

            <div className="sid-names">
                <FirstNameComponent
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    placeholder={
                        intl.formatMessage({
                            id: "firstName",
                            defaultMessage: "First Name",
                            })
                    }
                    onChange={
                        (newValue) => {
                            updateViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastNameComponent
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    placeholder={
                        intl.formatMessage({
                            id: "lastName",
                            defaultMessage: "Last Name",
                            })
                    }
                    onChange={
                        (newValue) => {
                            updateViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                placeholder={
                    intl.formatMessage({
                        id: "emailAddress",
                        defaultMessage: "Email Address",
                        })
                }
                explanation={
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                }
                onChange={
                    (newValue) => {
                        updateViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
            />

            <PostalCodeComponent
                isErrored={!!fieldValidationErrors.postalCode}
                placeholder={
                    intl.formatMessage({
                        id: "postalCode",
                        defaultMessage: "Home Zip Code",
                        })
                }
                onChange={(newValue: any) => {
                    updateViewModel(
                        'metadata',
                        {
                            postalCode: newValue,
                        },
                    );
                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                }}
                value={viewModel.hasOwnProperty('metadata') ? viewModel.metadata.postalCode : undefined}
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                onChange={(newValue) => {
                    updateViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
                verificationService={verificationService}
            />

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateViewModel("metadata", {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId("marketConsentValue", newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <button
                    onClick={submitForm}
                    type="submit"
                    className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                    aria-label="submit"
                >
                    <FormattedHTMLMessage id="verifyMyFirstResponderStatus" defaultMessage="VERIFY AND CONTINUE" />
                </button>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="firstResponderInfoShared"
                    defaultMessage={'Information entered here will be used for verification purposes.'}
                />
            </FormFooterComponent>
        </div>
    );
};


export const StepFirstResponderPersonalInfoComponent = injectIntl(StepFirstResponderPersonalInfo);
